import { createReducer, getType } from "typesafe-actions";

import chatSettings from "@app/util/chat/chatSettings";
import clampChatGeometry from "@app/util/chat/clampChatGeometry";
import { CHAT_PREFERENCE_DEFAULTS } from "@app/util/chat/consts";
import getChatDefaultGeometry from "@app/util/chat/getChatDefaultGeometry";

import {
  ChatActions,
  releaseChatHidden,
  requestChatHidden,
  setChatGeometry,
  setChatOpen,
  setChatPreferences,
  setChatSettingsOpen,
  toggleChatMaximized,
} from "./actions";
import type ChatState from "./ChatState";
import { ChatConversationActions } from "./conversation/actions";
import chatConversation, { chatConversationInitialState } from "./conversation/reducers";
import { ChatVoiceActions } from "./voice/actions";
import chatVoice, { chatVoiceInitialState } from "./voice/reducers";

const storedState = chatSettings.getState();
const { innerHeight, innerWidth } = window;

// Restored geometry is clamped against the viewport before first render, per spec —
// clamping only in useChatGeometry's effect (which runs after commit) lets an oversized
// restored window flash on screen for a frame before the effect corrects it.
// window.innerWidth/innerHeight are already available at module-eval time during app
// boot, so there is no need to wait for an effect.
//
// A window nobody has positioned yet has no stored rect at all: the default one is
// viewport-derived, so it is built here rather than baked into storage.
export const chatInitialState: ChatState = {
  conversation: chatConversationInitialState,
  geometry: storedState.geometry
    ? clampChatGeometry(storedState.geometry, innerWidth, innerHeight)
    : getChatDefaultGeometry(innerWidth, innerHeight),
  hideRequestCount: 0,
  isOpen: storedState.isOpen,
  isSettingsOpen: false,
  // Whatever storage could vouch for wins over the default, key by key. A stored value from
  // before preferences existed contributes nothing and leaves the defaults intact.
  preferences: { ...CHAT_PREFERENCE_DEFAULTS, ...storedState.preferences },
  voice: chatVoiceInitialState,
};

// Every branch below uses handleType (not handleAction): typesafe-actions' chain API only
// carries handleAction forward on handleAction and handleType forward on handleType, so once
// the child-forwarding handleType call is appended below, the parent's own cases have to be
// handleType too or the chain stops typechecking. mapLayers/reducers.ts hits the same
// constraint and resolves it the same way.
const chat = createReducer<ChatState, ChatActions | ChatConversationActions | ChatVoiceActions>(
  chatInitialState
)
  .handleType(getType(releaseChatHidden), (state) => ({
    ...state,
    hideRequestCount: Math.max(0, state.hideRequestCount - 1),
  }))
  .handleType(getType(requestChatHidden), (state) => ({
    ...state,
    hideRequestCount: state.hideRequestCount + 1,
  }))
  .handleType(getType(setChatGeometry), (state, { payload }) => ({ ...state, geometry: payload }))
  // Closing the window closes the settings panel with it, so reopening lands on the conversation
  // rather than on the settings the rep left behind.
  .handleType(getType(setChatOpen), (state, { payload }) => ({
    ...state,
    isOpen: payload,
    isSettingsOpen: payload ? state.isSettingsOpen : false,
  }))
  .handleType(getType(setChatPreferences), (state, { payload }) => ({
    ...state,
    preferences: { ...state.preferences, ...payload },
  }))
  .handleType(getType(setChatSettingsOpen), (state, { payload }) => ({
    ...state,
    isSettingsOpen: payload,
  }))
  // Maximizing banks the vertical rect it replaces; restoring puts it back and clears the
  // bank, which is also what marks the window as no longer maximized.
  .handleType(getType(toggleChatMaximized), (state, { payload }) => ({
    ...state,
    geometry: state.geometry.restore
      ? {
          ...state.geometry,
          height: state.geometry.restore.height,
          restore: undefined,
          top: state.geometry.restore.top,
        }
      : {
          ...state.geometry,
          height: payload.height,
          restore: { height: state.geometry.height, top: state.geometry.top },
          top: payload.top,
        },
  }))
  // Routes every action the child declares. Beware the landmine documented in
  // mapLayers/reducers.ts: if parent and child ever handle the same action type, the last
  // Object.keys(handlers) registration wins and the other silently never runs. Nothing
  // collides today because every child action is its own.
  .handleType(
    Object.keys(chatConversation.handlers) as ChatConversationActions["type"][],
    (state: ChatState, action: ChatConversationActions) => ({
      ...state,
      conversation: chatConversation(state.conversation, action),
    })
  )
  .handleType(
    Object.keys(chatVoice.handlers) as ChatVoiceActions["type"][],
    (state: ChatState, action: ChatVoiceActions) => ({
      ...state,
      voice: chatVoice(state.voice, action),
    })
  );

export default chat;
